import { EditReview, EditReviewAnalysis, PerformerContract, RolePerformerType } from '@talon/web/lib/api';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Card,
  CardBody,
  CardHeader,
  Carousel,
  CheckBox,
  Heading,
  Image,
  Text,
} from 'grommet';
import { Alert, Checkmark, Close, StatusGood } from 'grommet-icons';
import { PropsWithChildren } from 'react';
import { borderRadiusSize } from 'src/theme';
import { Divider } from '../../Divider';
import { ClassifiersList } from './ClassifiersList';
import { KnownAnalyses, Verdict } from './EditReviewModal';
import { ManualClassification } from './ManualClassification';

export type ContractedPerformersPanelProps = {
  knownAnalyses: KnownAnalyses;
  editReview?: EditReview;
  processingAction: string | null;
  setEnlargedFrameUrl: (val: string) => void;
  handleOutgrade: (
    editId: string,
    performerId: string,
    contractedType: RolePerformerType,
    reason?: string
  ) => Promise<void>;
  handleReclassify: (editId: string, performerId: string, newType: RolePerformerType, reason?: string) => Promise<void>;
  handleMarkInvalid: (editId: string, performerId: string, reason?: string) => Promise<void>;
};

export const ContractedPerformersPanel = ({
  knownAnalyses,
  editReview,
  processingAction = null,
  setEnlargedFrameUrl,
  handleOutgrade,
  handleReclassify,
  handleMarkInvalid,
}: ContractedPerformersPanelProps) => {
  /** Render **/

  const PerformerInfo = ({ contract, ...props }: BoxProps & { contract: PerformerContract }) => (
    <Box width="small" align="center" gap="small" {...props}>
      <Carousel controls={true} height="small" width="small" style={{ borderRadius: borderRadiusSize }}>
        {contract.characterPhotos?.map((photo, index) => (
          <Image key={`${contract.performer.id}_${index}`} src={photo.signedUrl} fit="cover" />
        ))}
      </Carousel>
      <Text>
        {contract.performer.last_name}, {contract.performer.first_name}
      </Text>
    </Box>
  );

  const PerformerFrames = ({
    analysis,
    frameUrls,
    ...props
  }: BoxProps & { analysis?: EditReviewAnalysis; frameUrls?: string[] }) => (
    <Box flex style={{ cursor: 'pointer', position: 'relative' }} {...props}>
      {analysis && analysis.frameAppears.length > 0 && (
        <Carousel height="medium" controls={true} style={{ borderRadius: borderRadiusSize }}>
          {frameUrls?.map((frameUrl) => (
            <Box key={frameUrl} onClick={() => setEnlargedFrameUrl(frameUrl)}>
              <Image src={frameUrl} />
              <Box margin="auto">
                <Text size="xsmall">🔍 Click image to enlarge</Text>
              </Box>
            </Box>
          ))}
        </Carousel>
      )}
      {(!analysis || analysis.frameAppears.length === 0) && !analysis?.outgraded && (
        <Box height="medium" align="center" justify="center" background={{ color: 'dark-2' }} round={borderRadiusSize}>
          <Text color="text-weak">Not detected</Text>
        </Box>
      )}
    </Box>
  );

  const VerdictCard = ({ children, ...props }: BoxProps & PropsWithChildren) => (
    <Card round="none" flex background="light-3" {...props}>
      {children}
    </Card>
  );

  const VerdictCardHeader = ({
    analysis,
    contract,
    verdict,
    ...props
  }: BoxProps & { analysis?: EditReviewAnalysis; contract: PerformerContract; verdict: Verdict }) => (
    <CardHeader
      direction="column"
      background={{
        color:
          verdict === Verdict.outgraded ? 'light-3' : verdict === Verdict.noChange ? 'status-ok' : 'status-warning',
      }}
      pad="small"
      gap="xsmall"
      align="start"
      {...props}
    >
      <Text>Hired as: {contract.role.performer_type}</Text>
      <Text weight="bold">Analysis result: {verdict}</Text>
      {analysis?.manuallyClassified && (
        <Box direction="row" gap="xsmall" align="center">
          <StatusGood size="small" color="brand" />
          <Text size="small" color="brand">
            Manually classified
          </Text>
        </Box>
      )}
      {analysis?.outgraded && (
        <Box direction="row" gap="xsmall" align="center">
          <Close size="small" color="status-critical" />
          <Text size="small" color="status-critical" weight="bold">
            Not in this edit
          </Text>
        </Box>
      )}
    </CardHeader>
  );

  const VerdictCardBody = ({ children, ...props }: BoxProps & PropsWithChildren) => (
    <CardBody
      border={[
        { side: 'vertical', size: 'small', color: 'light-4' },
        { side: 'bottom', size: 'small', color: 'light-4' },
      ]}
      {...props}
    >
      {children}
    </CardBody>
  );

  const OutgradedStatus = ({ analysis, ...props }: BoxProps & { analysis: EditReviewAnalysis }) => (
    <Box background={{ color: 'status-critical' }} pad="small" {...props}>
      <Text weight="bold" color="white">
        Outgraded - Not in This Edit
      </Text>
      {analysis.outgradeReason && (
        <Text size="small" color="white">
          Reason: {analysis.outgradeReason}
        </Text>
      )}
      {analysis.outgradedAt && (
        <Text size="xsmall" color="white">
          {new Date(analysis.outgradedAt).toLocaleString()}
        </Text>
      )}
    </Box>
  );

  const OutgradeButton = ({
    analysis,
    contract,
    ...props
  }: ButtonProps & { analysis?: EditReviewAnalysis; contract: PerformerContract }) => (
    <Button
      size="small"
      icon={<Close />}
      label={analysis?.outgraded ? 'Undo Outgrade' : 'Not in This Edit'}
      onClick={() =>
        handleOutgrade(
          editReview?.edit.id ?? '',
          contract.performer.id,
          contract.role.performer_type || RolePerformerType.Extra,
          'Performer did not make the final cut for this edit'
        )
      }
      disabled={processingAction === contract.performer.id}
      color={analysis?.outgraded ? 'status-ok' : 'status-critical'}
      {...props}
    />
  );

  const UpgradeToPrincipalButton = ({ analysis, ...props }: ButtonProps & { analysis: EditReviewAnalysis }) => (
    <Button
      size="small"
      icon={<Checkmark />}
      label="Upgrade to Principal"
      onClick={() =>
        handleReclassify(
          editReview?.edit.id || '',
          analysis.performerId,
          RolePerformerType.OnCameraPrincipal,
          'Manual upgrade from UI'
        )
      }
      disabled={processingAction === analysis.performerId}
      {...props}
    />
  );

  const DowngradeToExtraButton = ({ analysis, ...props }: ButtonProps & { analysis: EditReviewAnalysis }) => (
    <Button
      size="small"
      icon={<Close />}
      label="Downgrade to Extra"
      onClick={() =>
        handleReclassify(
          editReview?.edit.id || '',
          analysis.performerId,
          RolePerformerType.Extra,
          'Manual downgrade from UI'
        )
      }
      disabled={processingAction === analysis.performerId}
      {...props}
    />
  );

  const MarkInvalidButton = ({ analysis, ...props }: ButtonProps & { analysis: EditReviewAnalysis }) => (
    <Button
      size="small"
      icon={<Alert />}
      label="Mark Invalid"
      onClick={() => handleMarkInvalid(editReview?.edit.id || '', analysis.performerId, 'Marked as invalid from UI')}
      disabled={processingAction === analysis.performerId}
      secondary
      {...props}
    />
  );

  return (
    <Box direction="column" gap="medium" flex="grow">
      {/* Header */}
      <Box gap="xsmall">
        <Heading level={3} margin="none">
          Contracted Performers
        </Heading>
        <Divider height="2px" />
      </Box>

      {/* Body */}
      <Box>
        {knownAnalyses.map(({ contract, analysis, frameUrls, verdict }) => (
          <Card
            key={contract.id}
            gap="small"
            border={{ color: 'light-4', size: 'small' }}
            pad={{ horizontal: 'medium', bottom: 'medium', top: 'small' }}
            background="light-1"
            elevation="none"
            width="100%"
          >
            {/* Selection Checkbox */}
            <CardHeader pad={{ vertical: 'xxsmall' }}>
              <CheckBox />
            </CardHeader>

            <CardBody direction="row" gap="medium" align="start">
              {/* Photos */}
              <PerformerInfo contract={contract} />

              {editReview && (
                <Box flex gap="medium">
                  {/* Frames */}
                  {(analysis && analysis.frameAppears.length > 0) || !analysis?.outgraded ? (
                    <PerformerFrames analysis={analysis} frameUrls={frameUrls} />
                  ) : null}

                  {/* Verdict */}
                  <VerdictCard>
                    <VerdictCardHeader analysis={analysis} contract={contract} verdict={verdict} pad="small" />

                    {analysis?.outgraded ||
                      (analysis?.manuallyClassified && analysis.manualClassificationReason) ||
                      (analysis?.type === RolePerformerType.OnCameraPrincipal &&
                        (analysis.foreground ||
                          analysis.interactsWithProduct ||
                          analysis.reactsToNarration ||
                          analysis.voice) && (
                        <VerdictCardBody gap="xsmall">
                          {/* Show outgrade status and button for NOT DETECTED/OUTGRADED performers */}
                          {analysis?.outgraded && <OutgradedStatus analysis={analysis} />}

                          {/* Manual Classification Info */}
                          {analysis?.manuallyClassified && analysis.manualClassificationReason && (
                            <ManualClassification analysis={analysis} />
                          )}

                          {/* Classifiers List */}
                          {analysis?.type === RolePerformerType.OnCameraPrincipal &&
                              (analysis.foreground ||
                                analysis.interactsWithProduct ||
                                analysis.reactsToNarration ||
                                analysis.voice) && <ClassifiersList pad="small" analysis={analysis} />}
                        </VerdictCardBody>
                      ))}
                  </VerdictCard>

                  {/* Action Buttons */}
                  <Box gap="small" direction="row">
                    {(!analysis ||
                      analysis.outgraded ||
                      (analysis.frameAppears && analysis.frameAppears.length === 0)) && (
                      <OutgradeButton analysis={analysis} contract={contract} fill="horizontal" />
                    )}
                    {analysis && !analysis.invalidDetection && !analysis.outgraded && (
                      <>
                        {analysis.type === RolePerformerType.Extra && <UpgradeToPrincipalButton analysis={analysis} />}
                        {analysis.type === RolePerformerType.OnCameraPrincipal && (
                          <DowngradeToExtraButton analysis={analysis} fill="horizontal" />
                        )}
                        <MarkInvalidButton analysis={analysis} fill="horizontal" />
                      </>
                    )}
                  </Box>
                </Box>
              )}
            </CardBody>
          </Card>
        ))}
      </Box>
    </Box>
  );
};
